import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2, Users } from 'lucide-react';
import { type Lobby, fetchLobbies } from '~/api/lobbies';
import { buildHead } from '~/seo/site';
import { formatCountdown, useCountdown } from '~/ui/Countdown';
import { Badge } from '~/ui/components/badge';

type Filter = 'all' | Lobby['mode'];
const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'all' },
  { id: 'quickplay', label: 'quickplay' },
  { id: 'ranked', label: 'ranked' },
  { id: 'flip', label: 'sample flip' },
  { id: 'private', label: 'private (public)' },
  { id: 'daily', label: 'daily' },
];

type Search = { mode?: Filter };

export const Route = createFileRoute('/lobbies')({
  head: () =>
    buildHead({
      title: 'Live Lobbies',
      description:
        'Browse open beat-battle lobbies. Quickplay, ranked, sample flip, public private rooms, and the daily challenge - all in one list. Click any row to join.',
      path: '/lobbies',
    }),
  component: Lobbies,
  validateSearch: (search: Record<string, unknown>): Search => {
    const allowed: Filter[] = ['all', 'quickplay', 'ranked', 'flip', 'private', 'daily'];
    const m = search.mode;
    return {
      mode: typeof m === 'string' && (allowed as string[]).includes(m) ? (m as Filter) : undefined,
    };
  },
});

function Lobbies() {
  const { mode } = Route.useSearch();
  const navigate = useNavigate({ from: '/lobbies' });
  const filter: Filter = mode ?? 'all';

  const { data, isLoading, error } = useQuery({
    queryKey: ['lobbies'],
    queryFn: fetchLobbies,
    refetchInterval: 5_000,
  });

  const items = (data ?? []).filter((l) => filter === 'all' || l.mode === filter);

  return (
    <section className="mx-auto max-w-3xl px-4 py-10 space-y-6">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Live lobbies</h1>
        <p className="text-muted-foreground text-sm">
          Open matches you can join right now. Updates every few seconds.{' '}
          <Link to="/play" className="underline hover:text-foreground">
            Or start a new one
          </Link>
          .
        </p>
      </header>

      <nav
        aria-label="Filter lobbies by mode"
        className="flex flex-wrap gap-2 pb-1 font-mono text-[11px] uppercase tracking-wider"
      >
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() =>
              navigate({ search: f.id === 'all' ? {} : { mode: f.id }, replace: true })
            }
            aria-current={filter === f.id ? 'true' : undefined}
            className={`shrink-0 rounded-full border px-3 py-1 transition-colors ${
              filter === f.id
                ? 'border-primary bg-primary/15 text-foreground'
                : 'border-border bg-muted/30 text-muted-foreground hover:border-foreground/40 hover:text-foreground'
            }`}
          >
            {f.label}
          </button>
        ))}
      </nav>

      {isLoading && (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground">
          <Loader2 className="size-4 animate-spin inline-block mr-2" />
          Loading lobbies...
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm">
          Couldn't load lobbies: {String(error)}
        </div>
      )}

      {data && items.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-8 space-y-4">
          <div>
            <div className="text-lg font-medium">
              {filter === 'all' ? 'No open lobbies right now' : `No open ${filter} lobbies`}
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Lobbies fill up fast at peak hours. Two ways forward:
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link
              to="/play"
              className="rounded-md border border-primary/40 bg-primary/5 hover:bg-primary/10 p-4 text-sm space-y-1"
            >
              <div className="font-medium">Start a quickplay match</div>
              <div className="text-xs text-muted-foreground">
                Auto-fills as other producers join. 90-second countdown after 3 seated.
              </div>
            </Link>
            <Link
              to="/play"
              className="rounded-md border border-border hover:border-primary/40 p-4 text-sm space-y-1"
            >
              <div className="font-medium">Try the Daily Challenge</div>
              <div className="text-xs text-muted-foreground">
                Async beat to today's prompt. Vote on past Dailies any time.
              </div>
            </Link>
          </div>
        </div>
      )}

      <ul className="space-y-2">
        {items.map((l) => (
          <li key={l.matchId}>
            <Link
              to="/room/$code"
              params={{ code: l.roomCode }}
              className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card px-4 py-3 hover:border-primary"
            >
              <ModeBadge mode={l.mode} />
              <span className="font-mono text-xs text-muted-foreground">{l.roomCode}</span>
              <span className="text-sm">{l.genre.name}</span>
              {l.host && <span className="text-xs text-muted-foreground">@{l.host.handle}</span>}
              <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Users className="size-3.5" />
                {l.playerCount}/{l.capacity}
              </span>
              <CountdownPill lobbyStartsAt={l.lobbyStartsAt} />
              <span className="rounded-md bg-primary px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-primary-foreground">
                join
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CountdownPill({ lobbyStartsAt }: { lobbyStartsAt: string | null }) {
  const remaining = useCountdown(lobbyStartsAt);
  if (remaining === null) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-emerald-400"
      aria-label={`Match starts in ${formatCountdown(remaining)}`}
    >
      starts {formatCountdown(remaining)}
    </span>
  );
}

function ModeBadge({ mode }: { mode: Lobby['mode'] }) {
  const label: Record<Lobby['mode'], string> = {
    quickplay: 'quickplay',
    ranked: 'ranked',
    private: 'private',
    flip: 'flip',
    daily: 'daily',
    tournament: 'tournament',
  };
  return <Badge variant="secondary">{label[mode]}</Badge>;
}
